import { fetchAndFormatCapabilties } from './fetch-and-format-capabilities';
import type { CapabilitiesCache, QueuedRequest, UserId } from './UserCache';

/**
 * Shared batch execution logic used by both `useCapability` and
 * `prefetchCapabilities`. Flushes the pending capabilities from the
 * QueuedRequest, fetches them, updates the cache, and notifies subscribers.
 *
 * Capabilities requested but not returned by the API are removed from the
 * cache so they don't stay stuck as `'queued'` forever.
 */
export function executeBatch(
  queuedRequest: QueuedRequest,
  capabilitiesCache: CapabilitiesCache,
  fetchParams: { userId: UserId; jwt: string; apiUrl: string },
) {
  const capabilitiesToFetch = queuedRequest.flushCapabilities();
  if (!capabilitiesToFetch.length) return;

  fetchAndFormatCapabilties({
    capabilities: capabilitiesToFetch,
    ...fetchParams,
  })
    .then((response) => {
      capabilitiesCache.update(response);
      // Remove the 'queued' marker from capabilities the API didn't return
      // (the user doesn't have them).  We remove rather than default to
      // false so consumers that distinguish `undefined` (not yet loaded)
      // from `false` (loaded, user doesn't have it) keep working.
      const unreturned = capabilitiesToFetch.filter(
        (cap) => capabilitiesCache.currentCapabilities[cap] === 'queued',
      );
      if (unreturned.length) capabilitiesCache.remove(unreturned);
      queuedRequest.resolveSubscribers(capabilitiesCache);
    })
    .catch((error: Error | null) => {
      capabilitiesCache.remove(capabilitiesToFetch);
      queuedRequest.rejectSubscribers(error, capabilitiesToFetch);
    });
}
